/**
 * Location / station helpers for diary remarks.
 *
 * A `daily_diary_remark` carries the same four optional location fields (plus a design-context
 * `map_feature_link`) as a
 * `report_activity`: `latitude`, `longitude`, `location` (free-text label from
 * the pin picker) and `station_range` (jsonb). The form panel, the save payload
 * and the read-only summary cards all need to agree on how those are read and
 * written, so it lives here rather than inline in the components.
 */

import { MapFeatureLink, MapFeatureLinkPayload } from '../../../../../../shared/models/map-feature-link';
import { mergePickedLocation, recordLinkPayload } from '../../../../../../shared/helpers/design-context';

export interface RemarkLocationFields {
  latitude: any;
  longitude: any;
  location: string;
  station_range: any;
  /** The remark's design context (the api's serialized link), null when unlinked. */
  map_feature_link: MapFeatureLink | null;
}

export type RemarkLocationPayload = Omit<RemarkLocationFields, 'map_feature_link'> & {
  map_feature_link?: MapFeatureLinkPayload | null;
};

/** Server remark -> the location form controls (blank defaults, never undefined). */
export function readRemarkLocation(remark: any): RemarkLocationFields {
  return {
    latitude: remark?.latitude ?? '',
    longitude: remark?.longitude ?? '',
    location: remark?.location ?? '',
    station_range: remark?.station_range ?? null,
    map_feature_link: remark?.map_feature_link ?? null,
  };
}

/**
 * Form controls -> save payload. The api's fields are nullable; an untouched
 * control holds '' which is not a coordinate, so it goes out as null. The link
 * goes out in the api's input shape (never the serialized one); a pinless remark
 * sends null, and values without a link key leave the stored link untouched.
 */
export function remarkLocationPayload(values: Partial<RemarkLocationFields> | null | undefined, removed = false): RemarkLocationPayload {
  const latitude = blankToNull(values?.latitude);
  const longitude = blankToNull(values?.longitude);
  // `removed`: the link was removed in this session, so a null goes out as { sheet: null } (R54)
  const link = recordLinkPayload(values?.map_feature_link, latitude, longitude, removed);
  return {
    latitude,
    longitude,
    location: blankToNull(values?.location),
    station_range: values?.station_range ?? null,
    ...(link !== undefined ? { map_feature_link: link } : {}),
  };
}

/**
 * Shape a picker result for `FormGroup.patchValue`: the pin picker emits
 * `{latitude, longitude, location, locationText, map_feature_link?}` and the
 * station dialog emits `{station_range}` only, so each key is patched only when
 * the dialog actually sent it — picking a station must not wipe a pin or a link.
 */
export function normalizeLocationPayload(location: any): Partial<RemarkLocationFields> {
  return mergePickedLocation(location) as Partial<RemarkLocationFields>;
}

/** Same rule as the report editor's `showLocationSection()`. */
export function hasRemarkLocation(values: any): boolean {
  const hasGps = hasCoordinate(values?.latitude) && hasCoordinate(values?.longitude);
  // W50: a sheet-only drawing link has no pin, text or station but is still the remark's location
  return hasGps || !!values?.location || !!normalizeStationRange(values?.station_range) || !!values?.map_feature_link?.sheet;
}

/**
 * Which location picker the project uses — `pin` or `station_range` — resolved
 * the way `app-actions-section` does: the project's own setting, then its owner
 * organization's, then the app-wide daily-report location config.
 */
export function resolveDailyReportLocationMode(project: any, locationConfig: any): string | null {
  return [
    project?.daily_report_location_mode,
    project?.owner_organization?.daily_report_location_mode,
    locationConfig?.mode,
  ].find(value => ['pin', 'station_range'].includes(value)) ?? null;
}

/**
 * Whether the project carries alignments to pick stations on. Mobile gates its
 * station picker on exactly this (`!isEmpty(project.alignment_geometry)` in
 * formFeatureUtils.js) rather than on the location mode, and the web
 * `StationRangeComponent` reads the same column. The column is a GeoJSON
 * FeatureCollection stored as a string or an object; it counts as present when
 * it parses to a non-empty array, a collection with features, or a non-empty
 * object. '', 'null', '[]', '{}', null and unparseable strings are absent.
 */
export function projectHasAlignmentGeometry(project: any): boolean {
  const raw = project?.alignment_geometry ?? project?.alignmentGeometry;
  if (raw === null || raw === undefined) return false;
  let parsed = raw;
  if (typeof raw === 'string') {
    if (!raw.trim()) return false;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return false;
    }
  }
  if (parsed === null || typeof parsed !== 'object') return false;
  if (Array.isArray(parsed)) return parsed.length > 0;
  if ('features' in parsed) return Array.isArray(parsed.features) && parsed.features.length > 0;
  return Object.keys(parsed).length > 0;
}

/** A remark shaped for its read-only summary card (`app-activity-summary`). */
export interface RemarkSummaryCard {
  uuid: string;
  title: string;
  type: string;
  locationSummary: string;
  /** Rendered read-only by the card's `app-design-context` (spec §7.4: every record card). */
  map_feature_link: MapFeatureLink | null;
}

/** The served remark fields the card reads. */
export interface ServedRemark extends Partial<RemarkLocationFields> {
  id?: string;
  uuid?: string;
  title?: string | null;
  description?: string | null;
  type?: string | null;
}

export function remarkSummaryCard(remark: ServedRemark | null | undefined): RemarkSummaryCard {
  return {
    // The serializer emits the row's uuid as `id` — see the form component's
    // patchFormFromDiary for why that matters.
    uuid: remark?.uuid || remark?.id,
    // `title` holds the remark's body, not a short label: mobile treats it
    // as "the primary user-edited string per remark" and every row in the db
    // uses it that way (none populate `description`, and titles run past a
    // thousand characters). activity-summary clamps its title to two lines,
    // so a long remark stays inside the card.
    title: remark?.title || remark?.description || '',
    type: remark?.type || '',
    locationSummary: remarkLocationSummary(remark),
    map_feature_link: remark?.map_feature_link ?? null,
  };
}

/**
 * One line for a summary card, e.g. `BL Sta 10+00 – 12+50`, the pin's label,
 * or `61.21810, -149.90028` when the pin was dropped without a label. '' when
 * the remark has no location at all.
 */
export function remarkLocationSummary(remark: any): string {
  const station = stationRangeSummary(remark?.station_range);
  if (station) return station;
  if (remark?.location) return `${remark.location}`.trim();
  if (hasCoordinate(remark?.latitude) && hasCoordinate(remark?.longitude)) {
    return `${formatCoordinate(remark.latitude)}, ${formatCoordinate(remark.longitude)}`;
  }
  return '';
}

function stationRangeSummary(stationRange: any): string {
  const normalized = normalizeStationRange(stationRange);
  if (!normalized) return '';
  const stations = [normalized.from.station, normalized.to.station].filter(Boolean);
  if (!stations.length) {
    return normalized.alignment ? `${normalized.alignment}` : '';
  }
  const range = `Sta ${stations.join(' – ')}`;
  return normalized.alignment ? `${normalized.alignment} ${range}` : range;
}

/**
 * Both stored shapes: the web dialog's `{from:{station, offsetType,
 * offsetDistance}}` and mobile's `{from:{station, offset_type,
 * offset_distance}}`. Null when nothing inside is set.
 */
function normalizeStationRange(stationRange: any) {
  if (!stationRange || typeof stationRange !== 'object') return null;
  const from = stationRange.from || {};
  const to = stationRange.to || {};
  const normalized = {
    alignment: stationRange.alignment || null,
    from: {
      station: from.station || null,
      offsetType: from.offsetType ?? from.offset_type ?? null,
      offsetDistance: from.offsetDistance ?? from.offset_distance ?? null,
    },
    to: {
      station: to.station || null,
      offsetType: to.offsetType ?? to.offset_type ?? null,
      offsetDistance: to.offsetDistance ?? to.offset_distance ?? null,
    },
  };
  const hasValue = [
    normalized.alignment,
    normalized.from.station, normalized.from.offsetType, normalized.from.offsetDistance,
    normalized.to.station, normalized.to.offsetType, normalized.to.offsetDistance,
  ].some(value => value !== null && value !== undefined && value !== '');
  return hasValue ? normalized : null;
}

function hasCoordinate(value: any): boolean {
  return value !== null && value !== undefined && `${value}`.trim() !== '';
}

function formatCoordinate(value: any): string {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toFixed(5) : `${value}`;
}

function blankToNull(value: any): any {
  return value === '' || value === undefined ? null : value;
}
